import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  adminReturnLotterySetApi,
  adminConfirmOrderApi,
  getOrderByIdApi,
  getOrderLogsByOrderIdApi,
  updateNoteOrder,
} from "../../api/order";
import { getProfileUserByIdApi } from "../../api/user";
import Breadcrumb from "../../component/breadcrumb";
import ModalViewImage from "../../component/modal-view-image";
import { IOrder, IOrderLogs } from "../../interface/order";
import { IUser } from "../../interface/user";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppConfirm, useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import { getSrcImage } from "../../utils/utils";
import { isDealerMode } from "../../constant";
import useAppLoader from "../../component/hooks/useAppLoader";
import useAppSnackbar from "../../component/hooks/useAppSnackbar";

const OrderDetail = () => {
  let { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { confirm } = useAppConfirm();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<IOrder>();
  const [orderLogs, setOrderLogs] = useState<IOrderLogs[]>([]);
  const [showImage, setShowImage] = useState({ show: false, src: "" });
  const [user, setUser] = useState<IUser>();
  const [selectedAction, setSelectedAction] = useState<number>(1);
  const [note, setNote] = useState<string>("");
  const { showLoader, hideLoader } = useAppLoader();
  const { openErrorSnackbar, openSnackbar } = useAppSnackbar();

  const getStatus = (status?: number): ReactNode => {
    switch (status) {
      case 0:
        return <span className="text-danger">ยกเลิก/หมดเวลา</span>;
      case 1:
        return <span className="text-success">สำเร็จ</span>;
      case 2:
        return <span className="text-small">จอง</span>;
      case 3:
        return <span className="text-info">รอชำระเงิน</span>;
      case 4:
        return <span className="text-warning">รอยืนยัน</span>;
      case 5:
        return <span className="text-warning">รอยืนยัน</span>;
      default:
        return <span className="text-light">-</span>;
    }
  };

  const getOrderById = async (): Promise<IOrder> => {
    try {
      const { data } = await getOrderByIdApi(id!);
      setOrder(data);
      await getOrderLogsByOrderId(id!);
      return data;
    } catch (e: any) {
      throw e;
    }
  };

  const getProfileUserById = async (uid: string) => {
    try {
      const { data } = await getProfileUserByIdApi(uid);
      setUser(data);
    } catch (e: any) {
      throw e;
    }
  };

  const getOrderLogsByOrderId = async (id: string) => {
    try {
      const { data } = await getOrderLogsByOrderIdApi(id);
      setOrderLogs(data ?? []);
    } catch (e: any) {
      throw e;
    }
  };

  const initData = async () => {
    try {
      setLoading(true);
      const order = await getOrderById();
      await Promise.all([getProfileUserById(order.userId)]);

      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const confirmOrder = async (id: string, status: 0 | 1) => {
    try {
      setLoading(true);
      await adminConfirmOrderApi({ id, status });
      await Promise.all([getOrderById()]);
      openSnackbar("Success");
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const adminReturnLotterySet = async (lotterySetId: string) => {
    try {
      const isConfirmed = await confirm({
        title: "ยืนยันการทำรายการ",
        description: "ยืนยันการลบข้อมูล",
        textCancel: "ยกเลิก",
        classBtnCancel: "btn btn-light",
        textConfirm: "ยืนยัน",
        classBtnConfirm: "btn btn-danger",
      });

      if (!isConfirmed) return;

      setLoading(true);
      await adminReturnLotterySetApi(id!, { lotterySetId });
      await Promise.all([getOrderById()]);
      openSnackbar("Success");
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const onSave = async () => {
    if (note && note.length > 0) {
      showLoader();
      try {
        const res = await updateNoteOrder({ id: id!, note });
        setOrder(res.data);
        openSnackbar();
      } catch (e) {
        openErrorSnackbar(e);
      }
      hideLoader();
    }
  };

  useEffect(() => {
    initData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);
  return (
    <>
      <Breadcrumb
        title={"รายละเอียด"}
        children={[
          { title: "หน้าแรก", link: "/" },
          { title: "คำสั่งซื้อ", link: "/orders" },
          { title: "รายละเอียด" },
        ]}
      />

      <h4 className="mb-2">
        หมายเลขคำสั่งซื้อ {order?.orderNo ?? ""} {getStatus(order?.status)}{" "}
      </h4>
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="container_timeline">
                  <div className="timeline-block timeline-block-right mb-4">
                    <div className="marker active">
                      <i className="fa fa-user " aria-hidden="true"></i>
                    </div>
                    <div className="timeline-content border-bottom pb-4">
                      <div className="row">
                        <div className="col-12">
                          <div className="row">
                            <div className="col">
                              <span className="text-muted tx-13">ผู้ซื้อ</span>
                              <br />
                              <div
                                className="hover-underline cursor-pointer"
                                onClick={() =>
                                  isDealerMode()
                                    ? null
                                    : navigate(
                                        `${process.env.PUBLIC_URL}/users/edit/${order?.userId}`
                                      )
                                }
                              >
                                {user?.firstName} {user?.lastName}
                              </div>
                              <span className="tx-13 text-muted">
                                รหัส {user?.userNo}
                              </span>
                            </div>

                            <div className="col">
                              <span className="text-muted tx-13">
                                เบอร์โทรศัพท์
                              </span>
                              <br />
                              {user?.phoneNumber}
                            </div>
                            <div className="col">
                              <span className="text-muted tx-13">สถานะ</span>
                              <br />
                              {!!user
                                ? user?.status === 1
                                  ? "ใช้งาน"
                                  : "ระงับ"
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="timeline-block timeline-block-right ">
                    <div className="marker active">
                      <i className="fa fa-bank " aria-hidden="true"></i>
                    </div>
                    <div className="timeline-content ">
                      <div className="row">
                        <div className="col-12">
                          <div className="row">
                            <div className="col">
                              <span className="text-muted tx-13">ผู้ขาย</span>
                              <br />
                              <div
                                className="hover-underline cursor-pointer"
                                onClick={() =>
                                  isDealerMode()
                                    ? navigate(
                                        `${process.env.PUBLIC_URL}/profile`
                                      )
                                    : navigate(
                                        `${process.env.PUBLIC_URL}/dealers/profile/${order?.dealer.id}`
                                      )
                                }
                              >
                                {`${order?.dealer?.firstName} ${order?.dealer?.lastName} (${order?.dealer?.dealerInfo.name})`}
                              </div>
                              <span className="tx-13 text-muted">
                                รหัส {order?.dealerNo}
                              </span>
                            </div>

                            <div className="col">
                              <span className="text-muted tx-13">
                                เบอร์โทรศัพท์
                              </span>
                              <br />
                              {order?.dealer.phoneNumber}
                            </div>
                            <div className="col">
                              <span className="text-muted tx-13">สถานะ</span>
                              <br />
                              {!!order?.dealer
                                ? order?.dealer?.status === 1
                                  ? "ใช้งาน"
                                  : "ระงับ"
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <h6 className="text-muted">หมายเหตุ</h6>
          <div className="card">
            <div className="card-body">
              <div className="form-group row">
                <div className="col">
                  <textarea
                    name="note"
                    className="form-control ml-auto"
                    placeholder="เพิ่มหมายเหตุ"
                    style={{ maxWidth: "100%", minHeight: 150 }}
                    defaultValue={order?.note}
                    onChange={(e) => {
                      setNote(e.currentTarget.value ?? "");
                    }}
                  ></textarea>
                </div>
              </div>
              <div className="row d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-primary mr-2"
                  onClick={onSave}
                >
                  บันทึกข้อมูล
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <h6 className="text-muted">สถานะคำสั่งซื้อ</h6>
          <div className="card">
            <div className="card-body">
              <table className="table">
                <thead>
                  <tr>
                    <th>สถานะ</th>
                    <th>วันที่</th>
                    <th>เวลา</th>
                    <th>โดย</th>
                  </tr>
                </thead>
                <tbody>
                  {orderLogs.length > 0 ? (
                    orderLogs.map((logs) => {
                      return (
                        <tr key={logs.id}>
                          <td className="name">{logs.remark}</td>
                          <td className="unit">
                            {moment(logs.createdAt).format("DD/MM/yyyy")}
                          </td>
                          <td className="price">
                            {moment(logs.createdAt).format("HH:mm:ss")}
                          </td>
                          <td>{logs.createdBy}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        ไม่พบรายการ
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <div className="content">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}></th>
                        <th>เลข</th>
                        <th>จัดชุด/จำนวน</th>
                        <th>สกุลเงิน</th>
                        <th>ราคา(บาท)</th>
                        <th>อื่นๆ(บาท)</th>
                        <th>รวม(บาท)</th>
                        {/* {order?.currency === "KRW" ? (
                          <>
                            <th>ค่าเงิน THB/KRW</th>
                            <th>ราคา(₩)</th>
                            <th>อื่นๆ(₩)</th>
                            <th>รวม(₩)</th>
                          </>
                        ) : (
                          <>
                            <th>ราคา(บาท)</th>
                            <th>อื่นๆ(บาท)</th>
                            <th>รวม(บาท)</th>
                          </>
                        )} */}
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {order?.orderItems.map((item, index) => {
                        return (
                          <tr key={item.lotterySetId}>
                            <td>{index + 1}</td>
                            <td>
                              <span className="h5">
                                {item.lotterySet.number}
                              </span>
                            </td>
                            <td>{item.lotterySet.totalInSet} ใบ</td>
                            <td>{order.currency}</td>
                            <td>
                              {item.price.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td>
                              {item.serviceCharge.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td>
                              {(item.price + item.serviceCharge).toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>
                            {/* {order.currency !== "THB" ? (
                              <>
                                <td>{order.conversionRate.toFixed(2)}</td>
                                <td>
                                  {(
                                    item.price * order.conversionRate
                                  ).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                                <td>
                                  {(
                                    item.serviceCharge * order.conversionRate
                                  ).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                                <td>
                                  {(
                                    (item.price + item.serviceCharge) *
                                    order.conversionRate
                                  ).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </>
                            ) : (
                              <>
                                <td>
                                  {item.price.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                                <td>
                                  {item.serviceCharge.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                                <td>
                                  {(
                                    item.price + item.serviceCharge
                                  ).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </>
                            )} */}
                            <td>
                              {/* {[2, 3, 5].includes(order.status) && (
                                <button
                                  onClick={() => {
                                    adminReturnLotterySet(item.lotterySetId);
                                  }}
                                  className="btn btn-outline-primary  mb-1 pt-2"
                                >
                                  <span className="fa fa-trash-o"></span>
                                </button>
                              )} */}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="row text-right">
                <div className="col-12 border-bottom border-top pt-4 pb-4 ">
                  <span className="h5 ">
                    รวมทั้งหมด{" "}
                    {order?.totalLottery.toLocaleString("en-US") ?? 0} ใบ
                  </span>
                </div>
                <div className="col-12 border-bottom border-top pt-4 pb-4 ">
                  {/* {order?.currency === "KRW" ? (
                    <span className="h4">
                      ราคาทั้งหมด ₩
                      {(
                        (order?.totalAmount ?? 1) * (order?.conversionRate ?? 1)
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) ?? 0}
                    </span>
                  ) : (
                    <span className="h4">
                      ราคาทั้งหมด ฿
                      {order?.totalAmount.toLocaleString("en-US") ?? 0}
                    </span>
                  )} */}
                  <span className="h4">
                    ราคาทั้งหมด ฿
                    {order?.totalAmount.toLocaleString("en-US") ?? 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* {order?.paymentMethod === "โอนเงิน" && (
        <div className="row mb-4">
          <div className="col-12 ">
            <h6 className="text-muted">หลักฐานการชำระเงิน</h6>
            <div className="card ">
              <div className="card-body">
                <div className="col-lg-6 m-auto">
                  <div className="card">
                    <div
                      className="card-body text-center cursor-pointer"
                      onClick={() => {
                        setShowImage({
                          show: true,
                          src: getSrcImage(order?.slip),
                        });
                      }}
                    >
                      <img
                        src={getSrcImage(order?.slip)}
                        alt=""
                        style={{
                          height: 500,
                          width: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {order?.status === 5 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card ">
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-4">
                    <select
                      className="form-control"
                      value={selectedAction + ""}
                      onChange={(e) => {
                        setSelectedAction(+e.target.value);
                      }}
                    >
                      <option value="1">ยืนยัน</option>
                      <option value="0">ยกเลิก</option>
                    </select>
                  </div>
                  <button
                    className="btn btn-success ml-auto mr-2"
                    onClick={() => {
                      confirmOrder(order.id, selectedAction as 0 | 1);
                    }}
                  >
                    บันทึก
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      <ModalViewImage
        open={showImage.show}
        src={showImage.src}
        toggle={() => {
          setShowImage((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
    </>
  );
};
export default OrderDetail;
